import { ReactNode } from "react";

import Spinner, { SpinnerSize } from "../Spinner/Spinner";

export enum ButtonSize {
  large = "large",
  base = "base",
  small = "small",
}

export enum ButtonType {
  primary = "primary",
  neutral = "neutral",
  danger = "danger",
  transparent = "transparent",
}

export interface ButtonProps {
  children?: ReactNode;
  type?: ButtonType | `${ButtonType}`;
  size?: ButtonSize | `${ButtonSize}`;
  disabled?: boolean;
  loading?: boolean;
  icon?: ReactNode;
  iconPosition?: "left" | "right";
  rounded?: boolean;
  border?: boolean;
  onClick?: () => void;
}

const dimensions: Record<ButtonSize, string> = {
  large: "px-4 py-3 gap-2",
  base: "px-3 py-2.5 gap-1.5",
  small: "px-2 py-1.5 gap-1",
};

const dimensionsNoChildren: Record<ButtonSize, string> = {
  large: "p-3.5",
  base: "p-2.5",
  small: "p-1.5",
};

const typography: Record<ButtonSize, string> = {
  large: "typography-large-base",
  base: "typography-medium-base",
  small: "typography-medium-base",
};

const bgColors: Record<ButtonType, string> = {
  primary: "bg-button-primary-default hover:bg-button-primary-hover active:bg-button-primary-pressed focus:bg-button-primary-default",
  neutral: "bg-button-neutral-base hover:bg-button-neutral-hover active:bg-button-neutral-pressed focus:bg-button-neutral-base",
  danger: "bg-button-danger-base hover:bg-button-danger-hover active:bg-button-danger-pressed focus:bg-button-danger-base",
  transparent: "bg-button-transparent-base hover:bg-button-transparent-hover active:bg-button-transparent-pressed focus:bg-button-transparent-base",
};

const borderColors: Record<ButtonType, string> = {
  primary: "border-border-interactive-base hover:border-border-interactive-hover active:border-border-interactive-pressed focus:border-border-interactive-base",
  neutral: "border-border-strong hover:border-border-strong active:border-border-strong focus:border-border-strong",
  danger: "border-border-danger-base hover:border-border-danger-hover active:border-border-danger-pressed focus:border-border-danger-base",
  transparent: "border-border-transparent hover:border-border-strong active:border-border-strong focus:border-border-strong",
};

const textColors: Record<ButtonType, string> = {
  primary: "text-text-on-color",
  neutral: "text-text-base",
  danger: "text-text-on-color",
  transparent: "text-text-base",
};

const iconColors: Record<ButtonType, string> = {
  primary: "text-icon-on-color",
  neutral: "text-icon-bg-subtle",
  danger: "text-text-on-color",
  transparent: "text-icon-bg-subtle",
};

const loadingColors: Record<ButtonType, string> = {
  primary: "bg-button-primary-loading border-button-primary-loading text-icon-on-color",
  neutral: "bg-button-neutral-base border-border-strong icon-bg-subtle",
  danger: "bg-button-danger-loading border-button-danger-loading text-icon-on-color",
  transparent: "bg-button-transparent-base border-border-transparent icon-bg-subtle",
};

const loadingTextColors: Record<ButtonType, string> = {
  primary: "text-text-on-color",
  neutral: "text-text-subtle",
  danger: "text-text-on-color",
  transparent: "text-text-subtle",
};

const disabledColors: Record<ButtonType, string> = {
  primary: "bg-button-primary-disabled border-disabled text-icon-disabled",
  neutral: "bg-button-neutral-disabled border-strong text-icon-disabled",
  danger: "bg-button-primary-disabled border-disabled text-icon-disabled",
  transparent: "bg-button-transparent-disabled border-disabled text-icon-disabled",
};

const disabledTextColors: Record<ButtonType, string> = {
  primary: "text-text-disabled",
  neutral: "text-text-disabled",
  danger: "text-text-disabled",
  transparent: "text-text-disabled",
};

const Button = ({
  children, onClick, icon,
  type = ButtonType.primary, size = ButtonSize.base, disabled = false, loading = false, iconPosition = "left",
  rounded = true, border = true,
}: ButtonProps) => {
  const getColors = () => {
    if (loading) {
      return loadingColors[type];
    }
    if (disabled) {
      return disabledColors[type];
    }
    return `${bgColors[type]} ${borderColors[type]} ${iconColors[type]}`;
  };

  const getTextColors = () => {
    if (loading) {
      return loadingTextColors[type];
    }
    if (disabled) {
      return disabledTextColors[type];
    }
    return textColors[type];
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-fit items-center select-none
        ${rounded ? "rounded-md" : ""} ${border ? "border" : ""}
        ${getColors()} ${loading || disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
    >
      <div className={`flex ${children ? dimensions[size] : dimensionsNoChildren[size]}`}>
        {!loading && icon && iconPosition === "left" && icon}
        {loading && <Spinner size={SpinnerSize.xs} />}
        {children && <span className={`${getTextColors()} ${typography[size]}`}>{children}</span>}
        {icon && iconPosition === "right" && icon}
      </div>
    </button>
  );
};

export default Button;
